export const CANCELLATION_REASONS = [
  { value: "out_of_stock", label: "Out of stock" },
  { value: "duplicate_order", label: "Duplicate order" },
  { value: "incorrect_items", label: "Incorrect items/quantities" },
  { value: "branch_closed", label: "Branch closed" },
  { value: "order_placed_too_late", label: "Order placed too late" },
  { value: "no_longer_needed", label: "No longer needed" },
  { value: "other", label: "Other" },
] as const;

export type CancellationReasonValue = typeof CANCELLATION_REASONS[number]["value"];

export const BRANCH_RETURN_REASONS = [
  { code: "excess_stock", label: "Excess stock", autoDisposition: "back_to_stock" },
  { code: "near_expiry", label: "Near expiry", autoDisposition: null },
  { code: "expired", label: "Expired", autoDisposition: "write_off" },
  { code: "damaged", label: "Damaged", autoDisposition: "write_off" },
  { code: "wrong_item_received", label: "Wrong item received", autoDisposition: "back_to_stock" },
  { code: "seasonal_end", label: "End of season", autoDisposition: null },
  { code: "quality_issue", label: "Quality issue", autoDisposition: null },
  { code: "other", label: "Other", autoDisposition: null },
] as const;

export type BranchReturnReasonCode = typeof BRANCH_RETURN_REASONS[number]["code"];

export const SUPPLIER_RETURN_REASONS = [
  { code: "wrong_items_delivered", label: "Wrong items delivered", expectCredit: true as true | "negotiable" },
  { code: "damaged_on_arrival", label: "Damaged on arrival", expectCredit: true as true | "negotiable" },
  { code: "expired_on_arrival", label: "Expired / near-expiry on arrival", expectCredit: true as true | "negotiable" },
  { code: "quality_issue", label: "Quality doesn't meet standards", expectCredit: "negotiable" as true | "negotiable" },
  { code: "overdelivery", label: "Sent more than ordered", expectCredit: true as true | "negotiable" },
  { code: "supplier_recall", label: "Recalled by supplier", expectCredit: true as true | "negotiable" },
  { code: "other", label: "Other", expectCredit: "negotiable" as true | "negotiable" },
] as const;

export type SupplierReturnReasonCode = typeof SUPPLIER_RETURN_REASONS[number]["code"];

export const DISPOSITIONS = [
  { code: "back_to_stock", label: "Back to stock", inventoryImpact: "add" },
  { code: "write_off", label: "Write-off", inventoryImpact: "none" },
  { code: "quarantine", label: "Quarantine (pending inspection)", inventoryImpact: "none" },
  { code: "credit_pending", label: "Credit pending (supplier return)", inventoryImpact: "none" },
] as const;

export type DispositionCode = typeof DISPOSITIONS[number]["code"];

export const SENDER_NON_FULFILLMENT_REASONS = [
  { code: "out_of_stock", label: "Out of stock" },
  { code: "expired_stock", label: "Expired stock" },
  { code: "quality_issue", label: "Quality issue" },
  { code: "allocation_limit", label: "Allocation limit" },
  { code: "supplier_shortage", label: "Supplier shortage" },
  { code: "other", label: "Other" },
] as const;

export type SenderNonFulfillmentReasonCode = typeof SENDER_NON_FULFILLMENT_REASONS[number]["code"];

export const RECEIVER_NON_FULFILLMENT_REASONS = [
  { code: "damaged", label: "Damaged" },
  { code: "expired", label: "Expired" },
  { code: "wrong_item", label: "Wrong item" },
  { code: "quality_issue", label: "Quality issue" },
  { code: "missing_from_shipment", label: "Missing from shipment" },
  { code: "other", label: "Other" },
] as const;

export type ReceiverNonFulfillmentReasonCode = typeof RECEIVER_NON_FULFILLMENT_REASONS[number]["code"];

// Order statuses whose quantities are treated as "outstanding / on order" and are
// deducted from a source location's available stock. These are the committed,
// post-approval, pre-delivery states. Orders still pending approval, and closed
// orders (delivered / rejected / cancelled), are intentionally NOT counted.
export const RESERVED_ORDER_STATUSES = [
  "approved",
  "pending_branch_acceptance",
  "branch_proposed_changes",
  "preparing",
  "fulfillment_in_progress",
  "shipped",
  "out_for_delivery",
] as const;

export type ReservedOrderStatus = typeof RESERVED_ORDER_STATUSES[number];

// Human-friendly labels for the counted statuses, used in UI notes/tooltips.
export const RESERVED_ORDER_STATUS_LABELS: Record<ReservedOrderStatus, string> = {
  approved: "Approved",
  pending_branch_acceptance: "Awaiting branch acceptance",
  branch_proposed_changes: "Branch proposed changes",
  preparing: "Preparing",
  fulfillment_in_progress: "In fulfillment",
  shipped: "Shipped",
  out_for_delivery: "Out for delivery",
};

// Short list of counted statuses for compact display.
export const RESERVED_ORDER_STATUS_LABEL_LIST = Object.values(RESERVED_ORDER_STATUS_LABELS).join(", ");

// Plain-language note explaining how "available" is computed, for UI surfaces.
export const AVAILABLE_STOCK_NOTE =
  `Available = stock on hand minus quantities on confirmed orders not yet delivered. ` +
  `Counted statuses: ${RESERVED_ORDER_STATUS_LABEL_LIST}. ` +
  `Orders still pending approval are not counted.`;
